import { CaptureTreeEntityRevision } from "@faro-lotv/service-wires";
import { type JSX } from "react";
import { AddClusterContextMenuActionItem } from "./add-cluster-context-menu-action-item";
import { DeleteNodeContextMenuActionItem } from "./delete-node-context-menu-action-item";
import { EditNameContextMenuActionItem } from "./edit-name-context-menu-action-item";
import { FixReleaseContextMenuActionItem } from "./fix-release-context-menu-action-item";
import { LockClusterContextMenuActionItem } from "./lock-cluster-context-menu-action-item";
import { FocusOnContextMenuActionItem } from "./recenter-context-menu-action-item";
import { SortDescendantsContextMenuActionItem } from "./sort-descendants-context-menu-action-item";
import { VisibilityToggleContextMenuActionItem } from "./visibility-toggle-context-menu-action-item";

export enum ScanTreeContextMenuAction {
  editName = "editName",
  addCluster = "addCluster",
  fixRelease = "fixRelease",
  focusOn = "focusOn",
  delete = "delete",
  visibility = "visibility",
  lockCluster = "lockCluster",
  sortDescendants = "sortDescendants",
}

type ScanTreeContextMenuActionItemProps = {
  action: ScanTreeContextMenuAction;
  /** The identifier of the element which the context menu is for. */
  entity: CaptureTreeEntityRevision;
  /** Whether the context menu action item should be disabled.*/
  disabled?: boolean;
  /** Callback to execute when the action is clicked. */
  onContextMenuItemActionPerformed(): void;
  /** Callback executed to expand a tree node by ID. */
  onExpandNode?(entityId: CaptureTreeEntityRevision["id"]): void;
};

/**
 * @returns An entry in the context menu for a specific action.
 */
export function ScanTreeContextMenuActionItem({
  action,
  entity,
  disabled,
  onContextMenuItemActionPerformed,
  onExpandNode,
}: ScanTreeContextMenuActionItemProps): JSX.Element | null {
  switch (action) {
    case ScanTreeContextMenuAction.visibility:
      return (
        <VisibilityToggleContextMenuActionItem
          entity={entity}
          disabled={disabled}
        />
      );
    case ScanTreeContextMenuAction.editName:
      return (
        <EditNameContextMenuActionItem
          entity={entity}
          onNameEdited={onContextMenuItemActionPerformed}
          disabled={disabled}
        />
      );
    case ScanTreeContextMenuAction.addCluster:
      return (
        <AddClusterContextMenuActionItem
          clusterEntity={entity}
          onClusterAddition={onContextMenuItemActionPerformed}
          onExpandParentNode={onExpandNode}
          disabled={disabled}
        />
      );
    case ScanTreeContextMenuAction.fixRelease:
      return (
        <FixReleaseContextMenuActionItem
          entity={entity}
          disabled={disabled}
          onSuccess={onContextMenuItemActionPerformed}
        />
      );

    case ScanTreeContextMenuAction.focusOn:
      return (
        <FocusOnContextMenuActionItem
          entity={entity}
          onFocus={onContextMenuItemActionPerformed}
          disabled={disabled}
        />
      );
    case ScanTreeContextMenuAction.delete:
      return (
        <DeleteNodeContextMenuActionItem
          onDelete={onContextMenuItemActionPerformed}
          disabled={disabled}
        />
      );
    case ScanTreeContextMenuAction.lockCluster:
      return (
        <LockClusterContextMenuActionItem
          entity={entity}
          onLockToggled={onContextMenuItemActionPerformed}
          disabled={disabled}
        />
      );
    case ScanTreeContextMenuAction.sortDescendants:
      return (
        <SortDescendantsContextMenuActionItem
          entity={entity}
          onSortStarted={onContextMenuItemActionPerformed}
          disabled={disabled}
        />
      );
  }
}
